import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Close as CloseIcon,
  ContentCut as ContentCutIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayArrowIcon,
  QrCode2 as QrCode2Icon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Send as SendIcon,
  WarehouseOutlined as StoresIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/ui';
import ProductsTabs from '../../components/products/ProductsTabs';
import MsStoresDialog from '../../components/products/MsStoresDialog';
import { MsBulkForcedDialog, MsProductForcedDialog } from '../../components/products/MsForcedDialog';
import MsLinkDialog from '../../components/products/MsLinkDialog';
import MsBarcodeMatchDialog from '../../components/products/MsBarcodeMatchDialog';
import MsProductList from '../../components/products/MsProductList';
import MsProductDetail from '../../components/products/MsProductDetail';
import { ActiveFilterChips, MsProductFiltersButton } from '../../components/products/MsProductFilters';
import { storeOptions } from '../../components/products/msAssortmentApi.mjs';
import {
  FILTER_DEFAULTS,
  MP_LABEL,
  SEGMENTS,
  broadcastResultText,
  errorText,
  formatSyncTime,
  listParams,
  sameLink,
  shopLabel,
} from '../../components/products/msProductsModel';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const REPLENISH_POLL_MS = 1500;

function notifyReplenishResult(notify, data, shop) {
  if (data?.skipped) {
    notify.warning(data.message || `Пропуск: ${data.reason}`);
    return;
  }
  const errCount = Array.isArray(data?.errors) ? data.errors.length : 0;
  const msg = `${shopLabel(shop)}: заданий ${data?.created_tasks?.length ?? data?.tasks ?? 0}, строк ${data?.productions ?? 0}, перемещений ${data?.created_moves?.length ?? data?.moves ?? 0}`;
  if (errCount) notify.warning(`${msg}. Ошибок: ${errCount}`);
  else notify.success(`Пополнение записано в МойСклад. ${msg}`);
}

function notifyStocksSent(notify, data, fallbackCount) {
  const sent = `Остатки отправлены: ${data?.items ?? data?.skusSent ?? fallbackCount} позиций`;
  const skippedMs = Array.isArray(data?.skipped_archived_ms) ? data.skipped_archived_ms.length : 0;
  const skippedCards = Array.isArray(data?.skipped_cards) ? data.skipped_cards.length : 0;
  if (skippedMs || skippedCards) {
    notify.warning(`${sent}. Пропущено: связей на архивные товары МС ${skippedMs}, карточек без живой карточки МП ${skippedCards}`);
  } else {
    notify.success(sent);
  }
}

function withLinkEnabled(links, link, enabled) {
  return (links || []).map((entry) => (sameLink(entry, link) ? { ...entry, enabled } : entry));
}

function rowFromDetail(row, detail) {
  return {
    ...row,
    name: detail.name,
    barcode: detail.barcode,
    min_stock: detail.min_stock,
    max_stock: detail.max_stock,
    processing_plan_id: detail.processing_plan_id,
    replenish_skip: detail.replenish_skip,
    ms_archived: detail.ms_archived,
    issues: detail.issues,
    links: detail.links,
    forced: detail.forced,
  };
}

export default function MsProducts() {
  const notify = useNotify();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [segment, setSegment] = useState('all');
  const [filters, setFilters] = useState({ ...FILTER_DEFAULTS });
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [unlinkedMpArticles, setUnlinkedMpArticles] = useState([]);
  const [syncState, setSyncState] = useState(null);
  const listSeq = useRef(0);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const [stockLoading, setStockLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [busy, setBusy] = useState(false);
  const detailSeq = useRef(0);

  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [massBusy, setMassBusy] = useState(false);
  const [sendingStocks, setSendingStocks] = useState(false);
  const [bulkForcedOpen, setBulkForcedOpen] = useState(false);
  const [forcedOpen, setForcedOpen] = useState(false);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [stores, setStores] = useState([]);
  const [runs, setRuns] = useState([]);
  const [savingStores, setSavingStores] = useState(false);

  const [linkDialog, setLinkDialog] = useState(null);
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [filterMenu, setFilterMenu] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const [replenishShop, setReplenishShop] = useState(null);
  const [replenishing, setReplenishing] = useState(false);
  const [replenishProgress, setReplenishProgress] = useState(null);
  const replenishPollRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setQ(qInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [qInput]);

  const loadList = useCallback(async ({ append = false, offset = 0 } = {}) => {
    const seq = ++listSeq.current;
    setLoading(true);
    try {
      const { data } = await axios.get('/ms-assortment/products', {
        params: listParams({ q, segment, filters, offset, limit: PAGE_SIZE }),
        silent: true,
      });
      if (seq !== listSeq.current) return;
      const next = Array.isArray(data?.rows) ? data.rows : [];
      setRows((prev) => (append ? [...prev, ...next.filter((r) => !prev.some((p) => p.ms_id === r.ms_id))] : next));
      setCount(Number(data?.count) || 0);
      setSyncState(data?.sync_state || null);
      setUnlinkedMpArticles(Array.isArray(data?.unlinked_mp_articles) ? data.unlinked_mp_articles : []);
    } catch (error) {
      if (seq === listSeq.current) notify.error(errorText(error, 'Не удалось загрузить товары МС'));
    } finally {
      if (seq === listSeq.current) setLoading(false);
    }
  }, [q, segment, filters, notify]);

  useEffect(() => {
    setCheckedIds(new Set());
    loadList();
  }, [loadList]);

  const loadSummary = useCallback(async (refresh = false) => {
    setSummaryLoading(true);
    try {
      const { data } = await axios.get('/ms-assortment/diagnostics/summary', {
        params: { refresh: refresh ? 1 : undefined },
        silent: true,
      });
      setSummary(data || null);
    } catch (error) {
      notify.error(errorText(error, 'Не удалось загрузить диагностику связей'));
    } finally {
      setSummaryLoading(false);
    }
  }, [notify]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadDetail = useCallback(async (msId, { quiet = false, includeStock = false } = {}) => {
    if (!msId) return null;
    const seq = ++detailSeq.current;
    if (!quiet) setDetailLoading(true);
    setDetailError(false);
    try {
      const { data } = await axios.get(`/ms-assortment/products/${encodeURIComponent(msId)}`, {
        params: { include_stock: includeStock ? 1 : undefined },
        silent: true,
      });
      if (seq !== detailSeq.current) return null;
      setDetail((prev) => (
        !includeStock && prev?.ms_id === data.ms_id && prev.available !== undefined
          ? { ...data, available: prev.available }
          : data
      ));
      setRows((list) => list.map((row) => (row.ms_id === data.ms_id ? rowFromDetail(row, data) : row)));
      return data;
    } catch (error) {
      if (seq !== detailSeq.current) return null;
      setDetailError(true);
      if (!quiet) setDetail(null);
      notify.error(errorText(error, 'Не удалось загрузить товар'));
      return null;
    } finally {
      if (seq === detailSeq.current) setDetailLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    if (!selectedId) {
      detailSeq.current += 1;
      setDetail(null);
      setDetailLoading(false);
      return;
    }
    setDetail((prev) => (prev?.ms_id === selectedId ? prev : null));
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  useEffect(() => {
    if (isMobile || selectedId || !rows.length) return;
    setSelectedId(rows[0].ms_id);
  }, [rows, isMobile, selectedId]);

  const afterChange = useCallback(async () => {
    loadSummary();
    if (selectedId) await loadDetail(selectedId, { quiet: true });
  }, [loadSummary, loadDetail, selectedId]);

  const reloadAll = useCallback(async () => {
    await Promise.all([loadList(), loadSummary(), selectedId ? loadDetail(selectedId, { quiet: true }) : null]);
  }, [loadList, loadSummary, loadDetail, selectedId]);

  const loadSettings = useCallback(async () => {
    try {
      const [{ data: st }, storesRes, runsRes] = await Promise.all([
        axios.get('/ms-assortment/settings'),
        axios.get('/mswb/ms-stores', { silent: true }).catch(() => ({ data: [] })),
        axios.get('/ms-assortment/runs', { params: { limit: 5 }, silent: true }).catch(() => ({ data: { rows: [] } })),
      ]);
      setSettings(st);
      setStores(storeOptions(storesRes.data));
      setRuns(Array.isArray(runsRes.data?.rows) ? runsRes.data.rows : []);
    } catch (error) {
      notify.error(errorText(error, 'Не удалось загрузить настройки'));
    }
  }, [notify]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const storeById = useMemo(() => new Map(stores.map((s) => [s.id, s])), [stores]);

  const saveStores = async () => {
    setSavingStores(true);
    try {
      const { data } = await axios.put('/ms-assortment/settings', settings);
      setSettings(data);
      notify.success('Настройки складов сохранены');
      setSettingsOpen(false);
    } catch (error) {
      notify.error(errorText(error, 'Не удалось сохранить настройки'));
    } finally {
      setSavingStores(false);
    }
  };

  const openStores = () => {
    loadSettings();
    setSettingsOpen(true);
  };

  // ---------- действия детали ----------

  const runDetailAction = async (fn, fallback) => {
    setBusy(true);
    try {
      await fn();
      await afterChange();
    } catch (error) {
      notify.error(errorText(error, fallback));
      if (selectedId) loadDetail(selectedId, { quiet: true });
    } finally {
      setBusy(false);
    }
  };

  const setLinkEnabled = async (link, enabled) => {
    if (!detail) return;
    const msId = detail.ms_id;
    setDetail((prev) => (prev ? { ...prev, links: withLinkEnabled(prev.links, link, enabled) } : prev));
    setRows((list) => list.map((row) => (row.ms_id === msId ? { ...row, links: withLinkEnabled(row.links, link, enabled) } : row)));
    try {
      await axios.patch(`/ms-assortment/products/${msId}/links`, {
        marketplace: link.marketplace,
        account_id: link.account_id,
        product_key: link.product_key,
        enabled,
      });
      afterChange();
    } catch (error) {
      notify.error(errorText(error, 'Не удалось изменить связь'));
      loadDetail(msId, { quiet: true });
    }
  };

  const removeLink = (link) => {
    if (!detail) return;
    const msId = detail.ms_id;
    setConfirm({
      title: 'Удалить связь?',
      text: `Карточка ${link.article || link.name || link.product_key} (${MP_LABEL[link.marketplace] || link.marketplace}) останется без привязки к товару МС.`,
      confirmLabel: 'Удалить',
      color: 'error',
      run: () => runDetailAction(async () => {
        await axios.delete(`/ms-assortment/products/${msId}/links`, {
          data: { marketplace: link.marketplace, account_id: link.account_id, product_key: link.product_key, ms_id: msId },
        });
        notify.success('Связь удалена');
      }, 'Не удалось отвязать'),
    });
  };

  const setBroadcastAll = (enabled) => runDetailAction(async () => {
    const { data } = await axios.patch('/ms-assortment/links', { ms_ids: [detail.ms_id], enabled });
    const updated = Array.isArray(data?.items) ? data.items.length : 0;
    const skipped = Array.isArray(data?.skipped) ? data.skipped.length : 0;
    const text = broadcastResultText(enabled, updated, skipped);
    if (skipped) notify.warning(text);
    else notify.success(text);
  }, 'Не удалось изменить трансляцию');

  const archivedBulk = (kind) => {
    if (!detail) return;
    const msId = detail.ms_id;
    const links = detail.links || [];
    if (!links.length) {
      notify.info('У товара нет связей');
      return;
    }
    if (kind === 'disable') {
      setConfirm({
        title: 'Выключить все связи?',
        text: `Будет выключена трансляция у связей: ${links.filter((l) => l.enabled).length} из ${links.length}.`,
        confirmLabel: 'Выключить',
        color: 'warning',
        run: () => runDetailAction(async () => {
          await axios.patch('/ms-assortment/links', { ms_ids: [msId], enabled: false });
          notify.success('Трансляция выключена');
        }, 'Не удалось выключить связи'),
      });
      return;
    }
    if (kind === 'delete') {
      const ids = links.map((l) => l.link_id).filter((id) => id != null);
      setConfirm({
        title: 'Удалить все связи?',
        text: `Будет удалено связей: ${ids.length}. Карточки МП останутся без привязки к товару МС.`,
        confirmLabel: 'Удалить',
        color: 'error',
        run: () => runDetailAction(async () => {
          const { data } = await axios.post('/ms-assortment/diagnostics/links/bulk', { action: 'delete', link_ids: ids });
          notify.success(`Удалено связей: ${data?.affected ?? 0}`);
          loadList();
        }, 'Не удалось удалить связи'),
      });
      return;
    }
    const targets = detail.rebind_targets || [];
    if (!targets.length) return;
    setConfirm({
      title: 'Перенести связи на живой товар?',
      text: `Все связи (${links.length}) переедут на выбранный товар с тем же состоянием трансляции; режимы остатка (принуд/живой/не транслировать) в кабинетах переносятся.`,
      confirmLabel: 'Перенести',
      color: 'primary',
      targets,
      targetId: targets[0].ms_id,
      run: (targetId) => runDetailAction(async () => {
        const items = links.map((l) => ({
          marketplace: l.marketplace,
          account_id: l.account_id,
          product_key: l.product_key,
          enabled: !!l.enabled,
          rebind_from: msId,
        }));
        const { data } = await axios.post(`/ms-assortment/products/${encodeURIComponent(targetId)}/links/batch`, { items });
        notify.success(`Перенесено связей: ${data?.saved ?? items.length}`);
        loadList();
      }, 'Не удалось перенести связи'),
    });
  };

  const clearForced = (bindingId) => runDetailAction(async () => {
    await axios.put(`/ms-assortment/products/${detail.ms_id}/forced`, { items: [{ binding_id: bindingId, mode: 'default', forced_stock: null }] });
    notify.success('Настройка остатка снята');
  }, 'Не удалось снять настройку остатка');

  const sendProductStocks = () => runDetailAction(async () => {
    const { data } = await axios.post('/ms-assortment/products/stocks/sync', { ms_ids: [detail.ms_id] });
    notifyStocksSent(notify, data, 1);
  }, 'Не удалось отправить остатки');

  const saveProductSettings = async (patch) => {
    if (!detail) return;
    setSavingSettings(true);
    try {
      await axios.put(`/ms-assortment/products/${detail.ms_id}`, patch);
      notify.success('Настройки товара сохранены');
      await loadDetail(detail.ms_id, { quiet: true });
    } catch (error) {
      notify.error(errorText(error, 'Не удалось сохранить'));
    } finally {
      setSavingSettings(false);
    }
  };

  const loadStock = async () => {
    if (!detail) return;
    setStockLoading(true);
    try {
      await loadDetail(detail.ms_id, { quiet: true, includeStock: true });
    } finally {
      setStockLoading(false);
    }
  };

  const detailActions = {
    notify,
    selectProduct: (msId) => { if (msId) setSelectedId(msId); },
    setLinkEnabled,
    removeLink,
    setBroadcastAll,
    archivedBulk,
    clearForced,
    sendStocks: sendProductStocks,
    openForced: () => setForcedOpen(true),
    openStores,
    openLinkDialog: (prefill) => detail && setLinkDialog({ row: detail, prefill: prefill || null }),
    openRebind: (link, target) => {
      if (!target?.ms_id) return;
      setLinkDialog({
        row: { ms_id: target.ms_id, name: target.name || target.ms_id },
        prefill: { marketplace: link.marketplace, account_id: link.account_id, product_key: link.product_key },
      });
    },
  };

  // ---------- массовые по отмеченным ----------

  const toggleCheck = (msId, checked) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(msId);
      else next.delete(msId);
      return next;
    });
  };

  const toggleCheckAll = (checked) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      for (const row of rows) {
        if (checked) next.add(row.ms_id);
        else next.delete(row.ms_id);
      }
      return next;
    });
  };

  const massSetLinksEnabled = async (enabled) => {
    const msIds = [...checkedIds];
    if (!msIds.length) return;
    setMassBusy(true);
    try {
      const { data } = await axios.patch('/ms-assortment/links', { ms_ids: msIds, enabled });
      const items = Array.isArray(data?.items) ? data.items : [];
      const blocked = Array.isArray(data?.skipped) ? data.skipped : [];
      const appliedMs = new Set([...items, ...blocked].map((item) => String(item.ms_id)));
      const applied = msIds.filter((id) => appliedMs.has(String(id))).length;
      const skipped = msIds.length - applied;
      if (blocked.length) {
        notify.warning(`Включено связей ${items.length}, пропущено ${blocked.length} (проблемные связи)${skipped ? `, товаров без связей: ${skipped}` : ''}`);
      } else if (skipped && applied) {
        notify.warning(`${enabled ? 'Включена' : 'Выключена'} трансляция: ${applied}, без связей: ${skipped}`);
      } else if (skipped && !applied) {
        notify.warning('У отмеченных товаров нет связей');
      } else {
        notify.success(enabled ? `Включена трансляция: ${applied} товаров` : `Выключена трансляция: ${applied} товаров`);
      }
      await reloadAll();
    } catch (error) {
      notify.error(errorText(error, 'Не удалось изменить трансляцию'));
    } finally {
      setMassBusy(false);
    }
  };

  const sendCheckedStocks = async () => {
    const msIds = [...checkedIds];
    if (!msIds.length) return;
    setSendingStocks(true);
    try {
      const { data } = await axios.post('/ms-assortment/products/stocks/sync', { ms_ids: msIds });
      notifyStocksSent(notify, data, msIds.length);
    } catch (error) {
      notify.error(errorText(error, 'Не удалось отправить остатки'));
    } finally {
      setSendingStocks(false);
    }
  };

  // ---------- массовые по фильтру проблем ----------

  const openBulkAction = async (action) => {
    setFilterMenu(null);
    if (!filters.problemCodes.length) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.get('/ms-assortment/diagnostics/links', {
        params: { code: filters.problemCodes.join(','), account_id: filters.accountId || undefined },
        silent: true,
      });
      const list = (Array.isArray(data?.rows) ? data.rows : [])
        .filter((l) => l.link_id != null && (action === 'delete' || l.enabled));
      if (!list.length) {
        notify.info(action === 'delete' ? 'Нет связей под фильтр' : 'Нет включённых связей под фильтр');
        return;
      }
      setBulkConfirm({ action, ids: list.map((l) => l.link_id) });
    } catch (error) {
      notify.error(errorText(error, 'Не удалось получить связи под фильтр'));
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkAction = async () => {
    if (!bulkConfirm) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post('/ms-assortment/diagnostics/links/bulk', {
        action: bulkConfirm.action,
        link_ids: bulkConfirm.ids,
      });
      notify.success(bulkConfirm.action === 'delete'
        ? `Удалено связей: ${data?.affected ?? 0}`
        : `Выключено связей: ${data?.affected ?? 0}`);
      setBulkConfirm(null);
      await reloadAll();
    } catch (error) {
      notify.error(errorText(error, 'Не удалось выполнить действие'));
    } finally {
      setBulkBusy(false);
    }
  };

  // ---------- автопополнение ----------

  const storesReady = Boolean(
    settings?.production_store_id
    && settings?.materials_store_id
    && (settings?.gp_store_ids || []).length,
  );
  const sewingStoresReady = Boolean(
    settings?.sewing?.production_store_id
    && settings?.sewing?.materials_store_id
    && (settings?.sewing?.gp_store_ids || []).length,
  );

  const stopReplenishPoll = useCallback(() => {
    if (replenishPollRef.current) {
      clearTimeout(replenishPollRef.current);
      replenishPollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopReplenishPoll(), [stopReplenishPoll]);

  const waitReplenishJob = useCallback((job) => new Promise((resolve, reject) => {
    const id = String(job?.job_id || '').trim();
    if (!id) {
      reject(new Error('Не удалось запустить пополнение'));
      return;
    }
    setReplenishProgress(job);
    const tick = async () => {
      try {
        const { data } = await axios.get(`/ms-assortment/jobs/${encodeURIComponent(id)}`, { silent: true });
        setReplenishProgress(data);
        if (data?.status === 'completed' || data?.status === 'failed') {
          resolve(data);
          return;
        }
        replenishPollRef.current = setTimeout(tick, REPLENISH_POLL_MS);
      } catch (error) {
        if (error?.response?.status === 404) {
          reject(new Error('Задание прервалось после перезапуска сервера. Запустите снова.'));
          return;
        }
        replenishPollRef.current = setTimeout(tick, REPLENISH_POLL_MS);
      }
    };
    tick();
  }), []);

  const runReplenish = async () => {
    const shop = replenishShop;
    if (!shop) return;
    setReplenishing(true);
    stopReplenishPoll();
    try {
      let job;
      try {
        const { data } = await axios.post('/ms-assortment/replenish', { dry_run: false, shop }, { silent: true });
        job = data;
      } catch (error) {
        const body = error?.response?.data;
        if (error?.response?.status === 409 && body?.details?.job_id) {
          if (body.details.shop && body.details.shop !== shop) {
            notify.warning('Автопополнение уже выполняется');
            return;
          }
          job = body.details;
        } else {
          throw error;
        }
      }
      const done = await waitReplenishJob(job);
      if (done.status === 'failed') {
        notify.error(done.error_message || 'Не удалось запустить пополнение');
        return;
      }
      setReplenishShop(null);
      setReplenishProgress(null);
      await loadSettings();
      notifyReplenishResult(notify, done.result || {}, shop);
    } catch (error) {
      notify.error(errorText(error, 'Не удалось запустить пополнение'));
    } finally {
      stopReplenishPoll();
      setReplenishing(false);
    }
  };

  // ---------- разметка ----------

  const labels = summary?.labels || {};
  const emptyText = q
    ? (unlinkedMpArticles.length ? 'Привязки к товару МС нет' : 'Ничего не найдено по товарам МС и артикулам МП')
    : 'Нет товаров под фильтр';
  const problemFilterText = filters.problemCodes.map((code) => labels[code]?.label || code).join(', ');
  const filterAccount = summary?.accounts?.find((a) => String(a.id) === String(filters.accountId));

  const panel = (
    <MsProductDetail
      detail={detail}
      loading={detailLoading}
      error={detailError}
      labels={labels}
      query={q}
      busy={busy}
      actions={detailActions}
      savingSettings={savingSettings}
      stockLoading={stockLoading}
      onSaveSettings={saveProductSettings}
      onLoadStock={loadStock}
    />
  );

  return (
    <>
      <ProductsTabs />
      <PageHeader
        title="Товары МойСклад"
        subtitle="Связи МП, техкарты, min/max и автопополнение остатков"
        breadcrumbs={[
          { label: 'Товары', path: '/analytics/products' },
          { label: 'Товары МС' },
        ]}
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              disabled={!storesReady || replenishing}
              onClick={() => setReplenishShop('production')}
            >
              Производство
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ContentCutIcon />}
              disabled={!sewingStoresReady || replenishing}
              onClick={() => setReplenishShop('sewing')}
            >
              Швейка
            </Button>
          </Stack>
        )}
      />

      <Stack spacing={2}>
        {settings && !settings.production_store_in_gp && settings.production_store_id && (
          <Alert severity="warning">
            Склад производства не входит в склады ГП. Ожидание задания не попадёт в «доступно», и cron может создать лишние задания.
          </Alert>
        )}
        {settings && !settings.sewing_store_in_gp && settings.sewing?.production_store_id && (
          <Alert severity="warning">
            Склад швейки не входит в склады ГП швейки. Ожидание задания не попадёт в «доступно», и cron может создать лишние задания.
          </Alert>
        )}

        <Card variant="outlined">
          <CardContent sx={{ pb: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 0 }} alignItems="center">
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Название, ШК, id МС или артикул МП"
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  autoComplete="off"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                      ),
                      endAdornment: qInput ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setQInput('')} aria-label="Очистить поиск">
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    },
                  }}
                />
                <MsProductFiltersButton
                  filters={filters}
                  onChange={setFilters}
                  summary={summary}
                  summaryLoading={summaryLoading}
                  onRefreshSummary={() => { loadSummary(true); loadList(); }}
                />
              </Stack>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button startIcon={<QrCode2Icon />} onClick={() => setBarcodeOpen(true)}>Сопоставить по ШК</Button>
                <Tooltip title={checkedIds.size ? '' : 'Отметьте товары в списке'}>
                  <span>
                    <Button startIcon={<SendIcon />} disabled={!checkedIds.size || sendingStocks} onClick={sendCheckedStocks}>
                      {sendingStocks ? 'Отправляем…' : 'Отправить остатки'}
                    </Button>
                  </span>
                </Tooltip>
                <Button startIcon={<StoresIcon />} onClick={openStores}>Склады</Button>
                <Button endIcon={<ExpandMoreIcon />} onClick={(e) => setFilterMenu(e.currentTarget)} disabled={bulkBusy}>
                  По фильтру
                </Button>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={reloadAll} disabled={loading}>
                  Обновить
                </Button>
              </Stack>
            </Stack>
            <Stack spacing={1} sx={{ mt: 1 }}>
              <ActiveFilterChips filters={filters} onChange={setFilters} summary={summary} />
              {syncState?.finished_at && (
                <Typography variant="caption" color={syncState.complete === false ? 'warning.main' : 'text.secondary'}>
                  Товары МС обновлены {formatSyncTime(syncState.finished_at)}: живых {syncState.live ?? 0}, архивных {syncState.archived ?? 0}
                  {syncState.pruned ? `, удалено ${syncState.pruned}` : ''}
                  {syncState.complete === false ? ' — синк неполный, архивный статус мог не обновиться' : ''}
                </Typography>
              )}
              {checkedIds.size > 0 && (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                  <Typography variant="body2">Отмечено: {checkedIds.size}</Typography>
                  <Button size="small" disabled={massBusy} onClick={() => massSetLinksEnabled(true)}>Включить трансляцию</Button>
                  <Button size="small" disabled={massBusy} onClick={() => massSetLinksEnabled(false)}>Выключить трансляцию</Button>
                  <Button size="small" disabled={massBusy} onClick={() => setBulkForcedOpen(true)}>Остаток по складам</Button>
                  <Button size="small" color="inherit" onClick={() => setCheckedIds(new Set())}>Снять отметку</Button>
                </Stack>
              )}
            </Stack>
          </CardContent>
          <Tabs
            value={segment}
            onChange={(_, value) => setSegment(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1 }}
          >
            {SEGMENTS.map((item) => <Tab key={item.value} value={item.value} label={item.label} />)}
          </Tabs>
        </Card>

        {!loading && q && rows.length === 0 && unlinkedMpArticles.length > 0 && (
          <Alert severity="info">
            Артикул МП найден, но не привязан к товару МС:{' '}
            {unlinkedMpArticles.map((item) => `${MP_LABEL[item.marketplace] || item.marketplace}: ${item.offer_id}`).join(', ')}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <MsProductList
              rows={rows}
              count={count}
              loading={loading}
              selectedId={selectedId}
              onSelect={setSelectedId}
              checkedIds={checkedIds}
              onToggleCheck={toggleCheck}
              onToggleCheckAll={toggleCheckAll}
              onLoadMore={() => loadList({ append: true, offset: rows.length })}
              emptyText={emptyText}
            />
          </Grid>
          {!isMobile && (
            <Grid size={{ xs: 12, md: 8 }}>{panel}</Grid>
          )}
        </Grid>
      </Stack>

      {isMobile && (
        <Dialog fullScreen open={selectedId != null} onClose={() => setSelectedId(null)}>
          <DialogTitle sx={{ pr: 7 }}>
            {detail?.name || 'Товар'}
            <IconButton aria-label="Закрыть" onClick={() => setSelectedId(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>{panel}</DialogContent>
        </Dialog>
      )}

      <Menu anchorEl={filterMenu} open={Boolean(filterMenu)} onClose={() => setFilterMenu(null)}>
        {!filters.problemCodes.length && (
          <MenuItem disabled>
            <ListItemText primary="Выберите тип проблемы в «Фильтры»" secondary="Действие касается только проблемных связей" />
          </MenuItem>
        )}
        <MenuItem disabled={!filters.problemCodes.length} onClick={() => openBulkAction('disable')}>
          <ListItemText primary="Выключить связи с проблемами" secondary={problemFilterText || undefined} />
        </MenuItem>
        <MenuItem disabled={!filters.problemCodes.length} onClick={() => openBulkAction('delete')}>
          <ListItemText primary="Удалить связи с проблемами" secondary={problemFilterText || undefined} slotProps={{ primary: { color: 'error' } }} />
        </MenuItem>
      </Menu>

      <MsStoresDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        setSettings={setSettings}
        stores={stores}
        storeById={storeById}
        runs={runs}
        savingSettings={savingStores}
        onSaveReplenishment={saveStores}
        notify={notify}
      />
      <MsProductForcedDialog
        open={forcedOpen && !!detail}
        row={detail}
        onClose={() => setForcedOpen(false)}
        onSaved={afterChange}
        notify={notify}
      />
      <MsBulkForcedDialog
        open={bulkForcedOpen}
        msIds={[...checkedIds]}
        onClose={() => setBulkForcedOpen(false)}
        onSaved={reloadAll}
        notify={notify}
      />
      <MsLinkDialog
        open={!!linkDialog}
        row={linkDialog?.row || null}
        prefill={linkDialog?.prefill || null}
        onClose={() => setLinkDialog(null)}
        onSaved={reloadAll}
        notify={notify}
      />
      <MsBarcodeMatchDialog
        open={barcodeOpen}
        onClose={() => setBarcodeOpen(false)}
        onSaved={reloadAll}
        notify={notify}
      />

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>{confirm?.text}</Typography>
          {confirm?.targets?.length > 0 && (
            <FormControl size="small" fullWidth sx={{ mt: 2 }}>
              <InputLabel>Живой товар</InputLabel>
              <Select
                label="Живой товар"
                value={confirm.targetId}
                onChange={(e) => setConfirm((c) => ({ ...c, targetId: e.target.value }))}
              >
                {confirm.targets.map((t) => (
                  <MenuItem key={t.ms_id} value={t.ms_id}>
                    {t.name || t.ms_id}{t.source === 'ms_barcode' ? ' · тот же ШК в МС' : ' · по ШК карточки'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Отмена</Button>
          <Button
            variant="contained"
            color={confirm?.color || 'primary'}
            disabled={busy}
            onClick={async () => {
              const current = confirm;
              setConfirm(null);
              await current?.run(current.targetId);
            }}
          >
            {confirm?.confirmLabel || 'OK'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!bulkConfirm} onClose={() => !bulkBusy && setBulkConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{bulkConfirm?.action === 'delete' ? 'Удалить связи?' : 'Выключить связи?'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            {bulkConfirm?.action === 'delete'
              ? `Будет удалено связей: ${bulkConfirm?.ids?.length ?? 0}. Карточки МП останутся без привязки к товару МС.`
              : `Будет выключена трансляция у связей: ${bulkConfirm?.ids?.length ?? 0}.`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Под фильтр: {problemFilterText}
            {filters.accountId ? ` · кабинет ${filterAccount?.name || filters.accountId}` : ' · все кабинеты'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirm(null)} disabled={bulkBusy}>Отмена</Button>
          <Button
            variant="contained"
            color={bulkConfirm?.action === 'delete' ? 'error' : 'warning'}
            onClick={runBulkAction}
            disabled={bulkBusy}
          >
            {bulkConfirm?.action === 'delete' ? 'Удалить' : 'Выключить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(replenishShop)} onClose={() => !replenishing && setReplenishShop(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{replenishShop === 'sewing' ? 'Швейка' : 'Производство'}</DialogTitle>
        <DialogContent>
          {replenishShop === 'sewing' ? (
            <>
              <Typography sx={{ mt: 1 }}>
                Создать в МойСклад задания пошива и кроя по min/max чехлов и одно непроведённое перемещение ткани?
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                Запускайте после «Производство»: оно уже зарезервирует чехлы под диваны, и швейка добьёт свой min.
                В МойСклад чехлы часто стоят на «Основной техпроцесс» — контур ловит и тип (Швейка / Пошив / Крой),
                и имя карты «Пошив…» / «Крой…». «Нарезка» (поролон) остаётся в производстве. Для теста поставьте min/max на пару чехлов.
              </Alert>
            </>
          ) : (
            <>
              <Typography sx={{ mt: 1 }}>
                Создать в МойСклад производственные задания (диваны, наполнитель, резка, сборка) и одно непроведённое перемещение сырья?
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                Чехол и крой из техкарты дивана не запускаем в пошив — перемещаем со склада вместе с остальным сырьём.
                Ткань без техкарты уходит в перемещение швейки. После этого прогона запустите «Швейка», чтобы пополнить остатки чехлов.
              </Alert>
            </>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Ручной запуск сразу пишет в МойСклад и не зависит от тумблера cron.
            Создание может занять несколько минут — страница будет ждать в фоне, запрос не оборвётся.
          </Typography>
          {replenishing && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {replenishProgress?.message || 'Создаём в МойСклад…'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplenishShop(null)} disabled={replenishing}>Отмена</Button>
          <Button variant="contained" onClick={runReplenish} disabled={replenishing}>
            {replenishing ? 'Создаём…' : 'Создать в МойСклад'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
